import React, { Fragment, useEffect, useState } from "react";
import { Row, Table, Button, Col } from "reactstrap";
import { Link } from "react-router-dom";

import {
  Column,
  Table as ReactTable,
  ColumnFiltersState,
  FilterFn,
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  flexRender
} from '@tanstack/react-table';

import { rankItem } from '@tanstack/match-sorter-utils';
import JobListGlobalFilter from "./GlobalSearchFilter";

// Global Filter Input
const DebouncedInput = ({
  value: initialValue,
  onChange,
  debounce = 500,
  ...props
}: {
  value: string | number;
  onChange: (value: string | number) => void;
  debounce?: number;
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange'>) => {
  const [value, setValue] = useState(initialValue);

  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      onChange(value);
    }, debounce);
    return () => clearTimeout(timeout);
  }, [value, debounce]);

  return <input {...props} value={value} onChange={e => setValue(e.target.value)} />;
};

const Filter = ({ column, table }: { column: Column<any, unknown>; table: ReactTable<any>; }) => {
  const columnFilterValue = column.getFilterValue();
  return (
    <DebouncedInput
      type="text"
      value={(columnFilterValue ?? '') as string}
      onChange={value => column.setFilterValue(value)}
      placeholder="Search..."
      className="form-control"
    />
  );
};

interface TableContainerProps {
  columns?: any;
  data?: any;
  divClassName?: string;
  tableClass?: string;
  theadClass?: string;
  isBordered?: boolean;
  isGlobalFilter?: boolean;
  isPagination?: boolean;
  paginationWrapper?: string;
  SearchPlaceholder?: string;
  pagination?: string;
  handleUserClick?: () => void;
  buttonClass?: string;
  buttonName?: string;
  isAddButton?: boolean;
  isCustomPageSize?: boolean;
  isJobListGlobalFilter?: boolean;
  pageSize?: number;
}

const TableContainer = ({
  columns,
  data,
  tableClass,
  theadClass,
  divClassName,
  isBordered,
  isPagination,
  isGlobalFilter,
  paginationWrapper,
  SearchPlaceholder,
  pagination,
  buttonClass,
  buttonName,
  isAddButton,
  isCustomPageSize,
  handleUserClick,
  isJobListGlobalFilter,
}: TableContainerProps) => {
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [globalFilter, setGlobalFilter] = useState('');

  const fuzzyFilter: FilterFn<any> = (row, columnId, value, addMeta) => {
    const itemRank = rankItem(row.getValue(columnId), value);
    addMeta({ itemRank });
    return itemRank.passed;
  };

  const table = useReactTable({
    columns,
    data,
    filterFns: { fuzzy: fuzzyFilter },
    state: { columnFilters, globalFilter },
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    globalFilterFn: fuzzyFilter,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  const {
    getHeaderGroups,
    getRowModel,
    getCanPreviousPage,
    getCanNextPage,
    getPageOptions,
    setPageIndex,
    nextPage,
    previousPage,
    getState
  } = table;

  return (
    <Fragment>
      <Row className="mb-2 align-items-center">
        {isCustomPageSize && (
          <Col sm={2} className="mb-2">
            <select
              className="form-select"
              value={table.getState().pagination.pageSize}
              onChange={e => table.setPageSize(Number(e.target.value))}
            >
              {[10, 20, 30, 40, 50].map(pageSize => (
                <option key={pageSize} value={pageSize}>
                  Show {pageSize}
                </option>
              ))}
            </select>
          </Col>
        )}

        {isGlobalFilter && (
          <Col sm={4} className="mb-2">
            <DebouncedInput
              value={globalFilter ?? ''}
              onChange={value => setGlobalFilter(String(value))}
              className="form-control"
              placeholder={SearchPlaceholder}
            />
          </Col>
        )}

        {isJobListGlobalFilter && <JobListGlobalFilter setGlobalFilter={setGlobalFilter} />}

        {isAddButton && (
          <Col sm={6} className="text-sm-end mb-2">
            <Button type="button" className={buttonClass} onClick={handleUserClick}>
              <i className="mdi mdi-plus me-1"></i> {buttonName}
            </Button>
          </Col>
        )}
      </Row>

      <div className={divClassName || "table-responsive"}>
        <Table hover className={tableClass} bordered={isBordered}>
          <thead className={theadClass}>
            {getHeaderGroups().map(headerGroup => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map(header => (
                  <th key={header.id} colSpan={header.colSpan} className={header.column.columnDef.enableSorting ? "sorting" : ""}>
                    {header.isPlaceholder ? null : (
                      <>
                        <div
                          className={header.column.getCanSort() ? "cursor-pointer" : ""}
                          onClick={header.column.getToggleSortingHandler()}
                        >
                          {flexRender(header.column.columnDef.header, header.getContext())}
                        </div>
                        {header.column.getCanFilter() && (
                          <Filter column={header.column} table={table} />
                        )}
                      </>
                    )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>

          <tbody>
            {getRowModel().rows.map(row => (
              <tr key={row.id}>
                {row.getVisibleCells().map(cell => (
                  <td key={cell.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </Table>
      </div>

      {isPagination && (
        <Row className="mt-3 align-items-center">
          <Col sm={12} md={5}>
            {/* <div>Showing {getState().pagination.pageSize} of {data.length} Results</div> */}
            <div>Showing {getRowModel()?.rows.length} of {data.length} Results</div>
          </Col>
          <Col sm={12} md={7} className="text-md-end">
            <ul className={`pagination ${paginationWrapper || ''}`}>
              <li className={`page-item ${!getCanPreviousPage() && "disabled"}`}>
                <Link to="#" className="page-link" onClick={previousPage}><i className="mdi mdi-chevron-left"></i></Link>
              </li>
              {getPageOptions().map((item, key) => (
                <li key={key} className={`page-item ${getState().pagination.pageIndex === item ? "active" : ""}`}>
                  <Link to="#" className="page-link" onClick={() => setPageIndex(item)}>{item + 1}</Link>
                </li>
              ))}
              <li className={`page-item ${!getCanNextPage() && "disabled"}`}>
                <Link to="#" className="page-link" onClick={nextPage}><i className="mdi mdi-chevron-right"></i></Link>
              </li>
            </ul>
          </Col>
        </Row>
      )}
    </Fragment>
  );
};

export default TableContainer;
